package com.pincer.core

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.time.Clock

/*
As with logging, we set up all of this with the core so that it is available and standard on
both Client and Server side. Along with stuff in the contract, this means exceptions can be thrown
on the server side and caught on the client (and vice versa).
*/

@Serializable
open class PincerException(
    override val message: String = "Something went wrong on our side.",
): Exception(message) {
    open val code: Int = 500
    open val type: String = "PincerException"
    fun json() = exceptionJson.encodeToString(serializer(), this)
}

private val exceptionJson = Json { encodeDefaults = true }

open class ProblemWithResponse(
    override val message: String = "Something went wrong on our side. Its not you, its me.",
): PincerException(message) {
    override val code: Int = 500
    override val type: String = "ProblemWithResponse"
}

class LockAlreadyTaken(
    override val message: String = "Sorry, that item is already locked.",
): ProblemWithResponse(message) {
    override val code: Int = 400
    override val type: String = "LockAlreadyTaken"
}

open class ProblemWithRequest(
    override val message: String = "There was a problem with your request. Sorry we can't share more details.",
): PincerException(message) {
    override val code: Int = 400
    override val type: String = "ProblemWithRequest"
}

open class StaleSid(
    override val message: String = "That resource has changed.",
): ProblemWithRequest(message) {
    override val code: Int = 409
    override val type: String = "StaleSid"
}

open class YouNeedToLogIn(
    override val message: String = "You need to login.",
): ProblemWithRequest(message) {
    override val code: Int = 401
    override val type: String = "YouNeedToLogIn"
}

class YouAreNotAllowed(
    override val message: String = "Sorry, you don't have permissions to interact with that item.",
): ProblemWithRequest(message) {
    override val code: Int = 403
    override val type: String = "YouAreNotAllowed"
}

class DoesNotExist(
    override val message: String = "Sorry, the requested item does not exist.",
): ProblemWithRequest(message) {
    override val code: Int = 404
    override val type: String = "DoesNotExist"
}

class InvalidData(
    override val message: String = "Sorry, we've got a problem with some data that isn't valid.",
) : ProblemWithRequest(message) {
    override val code: Int = 400
    override val type: String = "InvalidData"
}

class LockedPrincipal(
    override val message: String = "Sorry, you need to wait a few minutes before trying that.",
) : ProblemWithRequest(message) {
    override val code: Int = 429
    override val type: String = "LockedPrincipal"
}

/* The message here is the new secret, hence no default. */
open class CycleSecret(
    override val message: String,
): ProblemWithRequest(message) {
    override val code: Int = 401
    override val type: String = "CycleSecret"
}

/*
Exceptions cross the wire as {"type", "code", "message"} and are rebuilt here as the right subclass.

We looked again at kotlinx.serialization's polymorphism (September 2026) and kept this:
 - class names aren't stable on JS (production builds minify them), so the type names have to be written out
 - a sealed hierarchy would stop code throwing a plain PincerException, which it does in many places
 - open polymorphism needs every subclass registered in a SerializersModule, which is this list by another name
ExceptionsTests round-trips every type, so add new subclasses there too.
*/
private val pincerExceptionTypes: Map<String, (String) -> PincerException> = mapOf(
    "ProblemWithResponse" to ::ProblemWithResponse,
    "LockAlreadyTaken"    to ::LockAlreadyTaken,
    "ProblemWithRequest"  to ::ProblemWithRequest,
    "StaleSid"            to ::StaleSid,
    "YouNeedToLogIn"      to ::YouNeedToLogIn,
    "YouAreNotAllowed"    to ::YouAreNotAllowed,
    "DoesNotExist"        to ::DoesNotExist,
    "InvalidData"         to ::InvalidData,
    "LockedPrincipal"     to ::LockedPrincipal,
    "CycleSecret"         to ::CycleSecret,
)

fun parsePincerException(json: String): PincerException {
    val e = Json.decodeFromString<PincerException>(PincerException.serializer(), json)
    return pincerExceptionTypes[e.type]?.invoke(e.message) ?: e
}
